import type { MailLink, PayslipContent, PayslipPrepare, TimesheetMailPrepare } from '@aop/domain';
import { config } from './config';
import { reportError } from './errorReport';
import { supabase } from './supabase';

export class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

/**
 * API trả 401 khi đã đăng nhập = phiên không còn hợp lệ (hết hạn, bị thu hồi, hoặc máy chủ vừa được khôi phục từ sao lưu).
 * Giao diện đăng ký một hàm để tự đăng xuất và đưa về màn hình đăng nhập, thay vì để người dùng gặp lỗi khó hiểu.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): () => void {
  onUnauthorized = fn;
  return () => { if (onUnauthorized === fn) onUnauthorized = null; };
}

async function request<T>(path: string, init: { method?: string; body?: unknown; auth?: boolean; headers?: Record<string, string> } = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers || {}) };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (init.auth !== false) {
    const { data } = await supabase().auth.getSession();
    if (data.session) headers.Authorization = `Bearer ${data.session.access_token}`;
  }
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, { method: init.method || 'GET', headers, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
  } catch {
    throw new ApiError('Không kết nối được máy chủ API. Kiểm tra mạng rồi thử lại.', 0);   // mất mạng: không báo lỗi
  }
  const json = await res.json().catch(() => ({})) as { error?: string };
  if (!res.ok) {
    if (res.status === 401 && init.auth !== false) onUnauthorized?.();
    if (res.status >= 500) reportError('api', `${init.method || 'GET'} ${path} → ${res.status} ${json.error ?? ''}`.trim());   // lỗi máy chủ mới báo; lỗi 4xx là do người dùng
    throw new ApiError(json.error || `Lỗi ${res.status}`, res.status);
  }
  return json as T;
}

export interface NewUserBody { username: string; fullName: string; email: string; phone?: string; title?: string; role: 'admin' | 'user'; modules: string[]; cos?: string[] }

/** Trạng thái link trên trang kích hoạt: ok | expired (hết hạn) | used (đã dùng) | replaced (bị link mới thay) | invalid. */
export type ActivationState = 'ok' | 'expired' | 'used' | 'replaced' | 'invalid';
export interface ActivationInfo { state: ActivationState; username?: string; fullName?: string; kind?: 'activation' | 'reset' }
export interface AttachmentDto { id: string; owner_type: 'employee' | 'document'; owner_id: string; kind: string; name: string; ext: string; size: number; storage_key: string }

export const api = {
  setup: (b: { fullName: string; username: string; email: string; password: string }, setupToken?: string) =>
    request<{ id: string }>('/setup', { method: 'POST', body: b, auth: false, headers: setupToken ? { 'x-setup-token': setupToken } : undefined }),
  /** Tạo người dùng: hệ thống gửi email chứa link kích hoạt (không có mật khẩu tạm). `mail.link` chỉ có khi thư không gửi đi được. */
  createUser: (b: NewUserBody) => request<{ id: string; username: string; mail: MailLink }>('/admin/users', { method: 'POST', body: b }),
  /** Gửi (lại) link kích hoạt (người đang chờ kích hoạt) hoặc link đặt lại mật khẩu (người đã kích hoạt). */
  sendLink: (id: string) => request<{ mail: MailLink }>(`/admin/users/${id}/send-link`, { method: 'POST' }),
  /** Sau khi đổi trạng thái (kích hoạt / ngừng kích hoạt): hệ thống tự gửi thư thông báo tới email của người đó. */
  notifyStatus: (id: string) => request<{ mail: MailLink }>(`/admin/users/${id}/notify-status`, { method: 'POST' }),
  deleteUser: (id: string) => request<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' }),

  /** Trang kích hoạt bằng link trong email (không cần đăng nhập). */
  activation: {
    info: (token: string) => request<ActivationInfo>(`/activate/${encodeURIComponent(token)}`, { auth: false }),
    setPassword: (token: string, password: string) => request<{ ok: true; username: string; status: string }>(`/activate/${encodeURIComponent(token)}`, { method: 'POST', body: { password }, auth: false }),
    resend: (token: string) => request<{ ok: true; to: string; dryRun: boolean }>(`/activate/${encodeURIComponent(token)}/resend`, { method: 'POST', auth: false }),
  },

  /** Gửi phiếu lương (PDF có mật khẩu) cho từng người sau khi chốt bảng lương. */
  payslipMail: {
    get: (company: string, month: string, content: PayslipContent = 'both') => request<PayslipPrepare>(`/admin/payroll-mail?company=${encodeURIComponent(company)}&month=${encodeURIComponent(month)}&content=${content}`),
    send: (b: { company: string; month: string; employeeIds?: string[]; force?: boolean; content?: PayslipContent }) => request<{ batchId: string; queued: number; mode: 'smtp' | 'log' }>('/admin/payroll-mail/send', { method: 'POST', body: b }),
    test: (b: { company: string; month: string; employeeId?: string; content?: PayslipContent }) => request<{ status: string; to: string; employee: { code: string; fullName: string }; password: string }>('/admin/payroll-mail/test', { method: 'POST', body: b }),
  },

  /** Gửi bảng chấm công (PDF có mật khẩu, không có số liệu lương) cho từng nhân viên được tick chọn. */
  timesheetMail: {
    get: (company: string, month: string) => request<TimesheetMailPrepare>(`/admin/timesheet-mail?company=${encodeURIComponent(company)}&month=${encodeURIComponent(month)}`),
    send: (b: { company: string; month: string; employeeIds: string[]; force?: boolean }) => request<{ batchId: string; queued: number; skipped: number; mode: 'smtp' | 'log' }>('/admin/timesheet-mail/send', { method: 'POST', body: b }),
    test: (b: { company: string; month: string; employeeId?: string }) => request<{ status: string; to: string; employee: { code: string; fullName: string }; password: string }>('/admin/timesheet-mail/test', { method: 'POST', body: b }),
  },

  /** Tải một tệp lên kho: xin link tạm → PUT thẳng lên kho → xác nhận để ghi siêu dữ liệu. */
  async uploadFile(file: File, owner: { type: 'employee' | 'document'; id: string }, kind: string): Promise<AttachmentDto> {
    const slot = await request<{ id: string; key: string; url: string; headers: Record<string, string> }>('/files/upload-url', {
      method: 'POST', body: { ownerType: owner.type, ownerId: owner.id, name: file.name, size: file.size, kind },
    });
    const put = await fetch(slot.url, { method: 'PUT', headers: slot.headers, body: file });
    if (!put.ok) throw new ApiError(`${file.name}: không tải lên được kho tệp (${put.status}).`, put.status);
    return request<AttachmentDto>('/files/confirm', { method: 'POST', body: { ownerType: owner.type, ownerId: owner.id, id: slot.id, key: slot.key, name: file.name, kind } });
  },
  fileUrl: (id: string, disposition: 'inline' | 'attachment' = 'inline') => request<{ url: string; name: string }>(`/files/${id}/url?disposition=${disposition}`),
  deleteFile: (id: string) => request<{ ok: true }>(`/files/${id}`, { method: 'DELETE' }),
};
